// Metadata-only catalog index (Requirement 9.4, 9.5).
//
// `MachineCoding.tsx` renders the problem list from this hand-authored array of
// `TemplateSummary` values and NEVER imports a Source_Tree. A new template is
// added by appending one summary here alongside its per-template module and its
// loader importer entry (the one documented authoring pattern).
//
// TODO: `rating` field is currently static/placeholder. Wire to real user
// ratings from the backend when the rating system is implemented. Until then,
// the UI hides it and shows "Difficulty" in the meta box instead.

import type { MachineCodingTemplate, TemplateSummary } from './types';

export type { TemplateSummary } from './types';

/**
 * Pure projection from a full template to its catalog summary. By construction
 * the result carries metadata only and never includes `fileStructure`.
 */
export function projectToSummary(t: MachineCodingTemplate): TemplateSummary {
 return {
  id: t.id,
  name: t.name,
  description: t.description,
  difficulty: t.difficulty,
  timeLimit: t.timeLimit,
  technologies: t.technologies,
  tags: t.tags,
  rating: t.rating,
 };
}

/**
 * Hand-authored catalog index for the existing templates. Authored alongside
 * each template module so the list renders without loading any body.
 */
export const machineCodingCatalogIndex: TemplateSummary[] = [
 {
  id: 'user-module-clean-architecture',
  name: 'User Module - Clean Architecture',
  description:
   'Implement a user module following clean architecture, SOLID principles, and common design patterns.',
  difficulty: 'intermediate',
  timeLimit: 90,
  technologies: ['Node.js', 'Express', 'TypeScript', 'REST API', 'In-Memory DB'],
  tags: ['Backend', 'API Design', 'Clean Architecture', 'SOLID Principles', 'Design Patterns'],
  rating: 4.7,
 },
 {
  id: 'splitwise-expense-sharing',
  name: 'SplitWise - Expense Sharing',
  description:
   'Build a complete expense sharing application like SplitWise with clean architecture, SOLID principles, and advanced balance calculations.',
  difficulty: 'hard',
  timeLimit: 120,
  technologies: ['Node.js', 'Express', 'TypeScript', 'Clean Architecture', 'Repository Pattern'],
  tags: ['Backend', 'Clean Architecture', 'SOLID Principles', 'Financial Logic', 'Design Patterns'],
  rating: 4.8,
 },
 {
  id: 'uber-ride-booking',
  name: 'Uber - Ride Booking System',
  description:
   'Design and implement a ride booking system similar to Uber with clean architecture: rider registration, driver onboarding, nearest-available-driver matching, fare calculation, and a full trip lifecycle.',
  difficulty: 'hard',
  timeLimit: 120,
  technologies: ['Node.js', 'Express', 'TypeScript', 'Clean Architecture', 'Strategy Pattern'],
  tags: ['Backend', 'Clean Architecture', 'Geolocation', 'Strategy Pattern', 'Design Patterns'],
  rating: 4.9,
 },
 {
  id: 'netflix-video-streaming',
  name: 'Netflix - Video Streaming Platform',
  description:
   'Build a video streaming platform with clean architecture: viewer profiles, catalog browsing, viewing-history recording, and genre-affinity recommendations.',
  difficulty: 'hard',
  timeLimit: 120,
  technologies: ['Node.js', 'Express', 'TypeScript', 'Clean Architecture', 'Strategy Pattern'],
  tags: ['Backend', 'Clean Architecture', 'Recommendations', 'Content Management', 'Design Patterns'],
  rating: 4.8,
 },
 {
  id: 'custom-blank',
  name: 'Custom Workspace',
  description:
   'A blank workspace. Start from a minimal scaffold and build any system you like — no predefined problem structure.',
  difficulty: 'easy',
  timeLimit: 60,
  technologies: ['Node.js', 'TypeScript'],
  tags: ['Sandbox', 'Custom'],
  rating: 5.0,
  isCustom: true,
 },
];
